import { useCallback, useEffect, useMemo, useState } from "react";
import { RequirementMonitor } from "@/services/backend/qpm/requirements/types/requirement-monitor";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import {
  Check,
  CircleHelp,
  MessageSquare,
  MoreHorizontal,
  Package,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Badge } from "@/shared/components/ui/badge";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { useRequirementEdit } from "@/services/backend/qpm/requirements/hooks/requirement-context";
import { v4 } from "uuid";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";
import {
  MonitorSnapDTO,
  normalizeMonitorAdditionalConfig,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { supportsIoTCheckpointService } from "@/services/backend/qpm/requirements/lib/derived-iot-dependents";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { MonitorSelectSearchComponent } from "@/routes/processes/[processId]/qpm/_components/monitor-select-search-component";

export type InventoryComponentProps = {
  requirement: RequirementDTO;
  areaIndex: number;
  areaId: string;
};

type HeaderInfoProps = {
  label: string;
  tooltip: string;
  align?: "start" | "center" | "end";
};

function HeaderInfo({ label, tooltip, align = "center" }: HeaderInfoProps) {
  const alignmentClass =
    align === "end"
      ? "justify-end"
      : align === "start"
        ? "justify-start"
        : "justify-center";
  return (
    <div className={`flex items-center gap-1 ${alignmentClass}`}>
      <span>{label}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            aria-label={`${label} Info`}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground h-5 w-5"
          >
            <CircleHelp className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="bg-background text-foreground max-w-sm border text-left text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export function MonitoringComponent({
  requirement,
  areaIndex,
  areaId,
}: InventoryComponentProps) {
  const { putRequest, updateRequirement } = useRequirementEdit();
  const [availableMonitors, setAvailableMonitors] = useState<MonitorSnapDTO[]>(
    [],
  );

  useEffect(() => {
    // Get all selected monitor IDs for this area
    const selectedMonitorIds = putRequest.requirement.selectedMonitors
      .filter((m) => m.areaId === areaId)
      .map((m) => m.monitorId);

    // Filter out already selected monitors from available monitors
    const filteredAvailableMonitors = Object.values(
      requirement.monitorSnaps || {},
    ).filter((monitor) => !selectedMonitorIds.includes(monitor.sourceId));

    setAvailableMonitors(filteredAvailableMonitors);
  }, [
    putRequest.requirement.selectedMonitors,
    areaId,
    requirement.monitorSnaps,
  ]);
  const [selectedMonitors, setSelectedMonitors] = useState<
    RequirementMonitor[]
  >([]);

  const { monitorSnaps: monitors } = requirement;

  useEffect(() => {
    const filteredMonitors = putRequest.requirement.selectedMonitors.filter(
      (m) => m.areaId === areaId,
    );
    setSelectedMonitors(filteredMonitors as RequirementMonitor[]);
  }, [putRequest.requirement.selectedMonitors, areaId]);

  const handleMonitorSelect = useCallback(
    (monitorId: string) => {
      const monitor = availableMonitors.find((m) => m.sourceId === monitorId);
      // instead of setting the existing stock amount to 0, find the amountOfMeasures in areaList area.amountOfMeasures where area.areaId === areaId
      const area = requirement.areaList.find((a) => a.areaId === areaId);

      if (monitor) {
        const iotClass = monitor.capabilities.iot?.class;
        const supportsCheckpointService =
          monitor.type === MonitorType.Checkpoint ||
          monitor.type === MonitorType.FlyExterminator ||
          (monitor.type === MonitorType.IoT &&
            supportsIoTCheckpointService(iotClass));
        const includeInCheckpointService =
          supportsCheckpointService &&
          monitor.capabilities.includeInCheckpointService;
        const selectedMonitor: RequirementMonitor = {
          id: v4(),
          areaId,
          monitorId: monitor.sourceId,
          monitorTypeKey: monitor.type,
          ioTSpec: monitor.capabilities.iot,
          includeInCheckpointService,
          hasLethalAgent: monitor.capabilities.hasLethalAgent,
          amount: 1,
          amountOfMeasures: area?.amountOfMeasures || 0,
          existingStockAmount: 0,
          selectedAdditions: [],
          deselectedDefaultAdditionIds: [],
          comment: "",
          position: "",
        };

        // Get all monitors for this area including the new one
        const currentAreaMonitors = [
          ...putRequest.requirement.selectedMonitors.filter(
            (m) => m.areaId === areaId,
          ),
          selectedMonitor,
        ];

        // Reassign positions for all monitors in this area
        const updatedMonitors = putRequest.requirement.selectedMonitors
          .filter((m) => m.areaId !== areaId) // Remove current area monitors
          .concat(
            currentAreaMonitors.map((m, idx) => ({
              ...m,
              position: `${areaIndex}.${idx + 1}`,
            })),
          );
        updateRequirement({ selectedMonitors: updatedMonitors });

        // Remove the selected monitor from availableMonitors
        setAvailableMonitors((prev) =>
          prev.filter((m) => m.sourceId !== monitorId),
        );
      }
    },
    [
      areaId,
      areaIndex,
      availableMonitors,
      putRequest.requirement.selectedMonitors,
      requirement.areaList,
      updateRequirement,
    ],
  );

  const handleDeleteArea = () => {
    if (selectedMonitors.length === 0) {
      const areas = putRequest.requirement.areaList.filter(
        (area) => area.areaId !== areaId,
      );
      updateRequirement({ areaList: areas });
    }
  };

  return (
    <PersistenceContextProvider>
      <TooltipProvider>
        <Table className="mb-2">
          <TableHeader>
            <TableRow>
              <TableHead>POS</TableHead>
              <TableHead>Monitor</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead className="text-right">{t("Anzahl")}</TableHead>
              <TableHead className="text-right">
                <HeaderInfo
                  label={t("davon Altbestand")}
                  align="end"
                  tooltip="Altbestand wird nicht für Material und Installation berechnet. Für Service gilt die Einstellung QPM Service. Soll nur ein Teil im QPM Service berechnet werden, den gleichen Monitor über das Menü als neue Zeile hinzufügen und getrennt konfigurieren."
                />
              </TableHead>
              <TableHead>{t("Zusätze")}</TableHead>
              <TableHead className="text-center">{t("Bemerkung")}</TableHead>
              <TableHead>
                <HeaderInfo
                  label={t("QPM Maßnahmen")}
                  align="end"
                  tooltip="Nur setzen, wenn für diesen Monitor eine abweichende Anzahl von Maßnahmen gebraucht wird. Sonst gelten die globalen Einstellungen in Zusätzliche Information für QPM, ALoRa und FIV."
                />
              </TableHead>
              <TableHead className="text-center">
                <HeaderInfo
                  label={t("QPM Service")}
                  align="center"
                  tooltip="Diese Einstellung wirkt auf die gesamte Zeile des Monitors, nicht nur auf Altbestand. Wenn aktiv, wird die Zeile im QPM Service mitberechnet. Verwendet werden globale QPM Maßnahmen oder bei Abweichung die individuellen Maßnahmen aus dieser Zeile. Für getrennte Behandlung von neu und Altbestand den gleichen Monitor als zweite Zeile hinzufügen."
                />
              </TableHead>
              <TableHead className="w-fit text-right">{t("Aktion")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {selectedMonitors.map((sm) => (
              <RequirementMonitorRow
                key={sm.id}
                sm={sm}
                monitorData={monitors[sm.monitorId]}
                areaIndex={areaIndex}
              />
            ))}
          </TableBody>
        </Table>
      </TooltipProvider>
      <div className="flex flex-col justify-between px-2 py-4 md:flex-row">
        <MonitorSelectSearchComponent
          monitorList={availableMonitors}
          onMonitorSelect={handleMonitorSelect}
        />
        <div className="flex justify-end gap-2">
          {selectedMonitors.length <= 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="gap-2" variant="destructive">
                  <Trash2 className="h-5 w-5" />
                  {t("Bereich entfernen")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("Bereich entfernen?")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t(
                      `Möchten Sie den Bereich aus der Bestandsaufahme entfernen?`,
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Abbruch")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteArea}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {t("Bestätigen")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <PersistenceStateUsingContext />
        </div>
      </div>
    </PersistenceContextProvider>
  );
}

export type RequirementMonitorRowProps = {
  sm: RequirementMonitor;
  monitorData: MonitorSnapDTO;
  areaIndex: number;
};

export function RequirementMonitorRow({
  sm,
  monitorData,
  areaIndex,
}: RequirementMonitorRowProps) {
  const { putRequest, updateRequirement } = useRequirementEdit();
  const availableAdditions = useMemo(
    () => monitorData.additionals ?? [],
    [monitorData.additionals],
  );
  const additionsById = useMemo(
    () =>
      new Map(availableAdditions.map((addition) => [addition.id, addition])),
    [availableAdditions],
  );
  const selectedAdditions = useMemo(
    () => sm.selectedAdditions ?? [],
    [sm.selectedAdditions],
  );
  const deselectedDefaultAdditionIds = useMemo(
    () => sm.deselectedDefaultAdditionIds ?? [],
    [sm.deselectedDefaultAdditionIds],
  );
  const [additionDialogOpen, setAdditionDialogOpen] = useState(false);
  const [draftAdditions, setDraftAdditions] =
    useState<RequirementMonitor["selectedAdditions"]>(selectedAdditions);
  const [
    draftDeselectedDefaultAdditionIds,
    setDraftDeselectedDefaultAdditionIds,
  ] = useState<string[]>(deselectedDefaultAdditionIds);
  const billableMaterialAmount = Math.max(
    (sm.amount || 0) - (sm.existingStockAmount || 0),
    0,
  );
  const reconcileConfiguredAdditionSelections = useCallback(
    (
      additions: RequirementMonitor["selectedAdditions"],
      deselectedDefaults: string[],
    ) => {
      const selectedById = new Set(additions.map((item) => item.additionalId));
      const deselectedDefaultIds = new Set(deselectedDefaults);
      const next = [...additions];
      for (const additional of availableAdditions) {
        if (selectedById.has(additional.id)) {
          continue;
        }
        const config = normalizeMonitorAdditionalConfig(additional.config);
        if (
          config.selectionMode === "ALWAYS_REQUIRED" ||
          (config.selectionMode === "DEFAULT_SELECTED_OPTIONAL" &&
            !deselectedDefaultIds.has(additional.id))
        ) {
          next.push({
            additionalId: additional.id,
            materialMode: "BOUND_TO_MONITOR_MATERIAL",
            separateAmount: billableMaterialAmount,
          });
        }
      }
      return next;
    },
    [availableAdditions, billableMaterialAmount],
  );

  const additionDisplayAmount = (
    addition: RequirementMonitor["selectedAdditions"][number],
    monitor: RequirementMonitor = sm,
  ) => {
    if (addition.materialMode === "SEPARATE_MATERIAL") {
      return addition.separateAmount || 0;
    }
    return Math.max(
      (monitor.amount || 0) - (monitor.existingStockAmount || 0),
      0,
    );
  };
  const effectiveSelectedAdditions = useMemo(
    () =>
      reconcileConfiguredAdditionSelections(
        selectedAdditions,
        deselectedDefaultAdditionIds,
      ),
    [
      deselectedDefaultAdditionIds,
      reconcileConfiguredAdditionSelections,
      selectedAdditions,
    ],
  );

  const handleChange = useCallback(
    ({
      monitor,
      amount,
      amountOfMeasures,
      existingStockAmount,
      selectedAdditions: nextSelectedAdditions,
      deselectedDefaultAdditionIds: nextDeselectedDefaultAdditionIds,
      comment,
    }: {
      monitor: RequirementMonitor;
      amount?: number;
      amountOfMeasures?: number;
      existingStockAmount?: number;
      selectedAdditions?: RequirementMonitor["selectedAdditions"];
      deselectedDefaultAdditionIds?: string[];
      comment?: string;
    }) => {
      const updatedSelectedMonitors =
        putRequest.requirement.selectedMonitors.map((m) => {
          if (m.id === monitor.id) {
            const nextMonitor = {
              ...m,
              amount: amount ?? m.amount,
              amountOfMeasures: amountOfMeasures ?? m.amountOfMeasures,
              existingStockAmount: existingStockAmount ?? m.existingStockAmount,
              selectedAdditions:
                nextSelectedAdditions ?? m.selectedAdditions ?? [],
              deselectedDefaultAdditionIds:
                nextDeselectedDefaultAdditionIds ??
                m.deselectedDefaultAdditionIds ??
                [],
              comment: comment ?? m.comment,
              includeInCheckpointService:
                monitor.includeInCheckpointService ??
                m.includeInCheckpointService,
            };
            const nextBillableMaterialAmount = Math.max(
              (nextMonitor.amount || 0) -
                (nextMonitor.existingStockAmount || 0),
              0,
            );
            const resetAdditionAmounts = amount !== undefined;
            return {
              ...nextMonitor,
              selectedAdditions: nextMonitor.selectedAdditions.map(
                (addition) =>
                  resetAdditionAmounts
                    ? {
                        ...addition,
                        materialMode: "BOUND_TO_MONITOR_MATERIAL" as const,
                        separateAmount: nextBillableMaterialAmount,
                      }
                    : addition.materialMode === "BOUND_TO_MONITOR_MATERIAL"
                      ? {
                          ...addition,
                          separateAmount: nextBillableMaterialAmount,
                        }
                      : addition,
              ),
            };
          }
          return m;
        });
      updateRequirement({
        selectedMonitors: updatedSelectedMonitors,
      });
    },
    [putRequest.requirement.selectedMonitors, updateRequirement],
  );

  const handleAdditionDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setDraftAdditions(
          reconcileConfiguredAdditionSelections(
            selectedAdditions,
            deselectedDefaultAdditionIds,
          ),
        );
        setDraftDeselectedDefaultAdditionIds(deselectedDefaultAdditionIds);
      }
      setAdditionDialogOpen(open);
    },
    [
      deselectedDefaultAdditionIds,
      reconcileConfiguredAdditionSelections,
      selectedAdditions,
    ],
  );

  const handleDraftAdditionSelect = useCallback(
    (additionalId: string) => {
      setDraftAdditions((current) => {
        if (current.some((item) => item.additionalId === additionalId)) {
          return current;
        }
        return [
          ...current,
          {
            additionalId,
            materialMode: "BOUND_TO_MONITOR_MATERIAL",
            separateAmount: billableMaterialAmount,
          },
        ];
      });
      const additional = additionsById.get(additionalId);
      const config = normalizeMonitorAdditionalConfig(additional?.config);
      if (config.selectionMode === "DEFAULT_SELECTED_OPTIONAL") {
        setDraftDeselectedDefaultAdditionIds((current) =>
          current.filter((id) => id !== additionalId),
        );
      }
    },
    [additionsById, billableMaterialAmount],
  );

  const handleDraftAdditionAmountChange = useCallback(
    (additionalId: string, amount: number) => {
      setDraftAdditions((current) =>
        current.map((item) =>
          item.additionalId === additionalId
            ? {
                ...item,
                materialMode:
                  amount === billableMaterialAmount
                    ? "BOUND_TO_MONITOR_MATERIAL"
                    : "SEPARATE_MATERIAL",
                separateAmount: amount,
              }
            : item,
        ),
      );
    },
    [billableMaterialAmount],
  );

  const handleDraftAdditionRemove = useCallback(
    (additionalId: string) => {
      const additional = additionsById.get(additionalId);
      const config = normalizeMonitorAdditionalConfig(additional?.config);
      if (config.selectionMode === "ALWAYS_REQUIRED") {
        return;
      }
      setDraftAdditions((current) =>
        current.filter((item) => item.additionalId !== additionalId),
      );
      if (config.selectionMode === "DEFAULT_SELECTED_OPTIONAL") {
        setDraftDeselectedDefaultAdditionIds((current) =>
          current.includes(additionalId) ? current : [...current, additionalId],
        );
      }
    },
    [additionsById],
  );

  const handleAdditionDialogConfirm = useCallback(() => {
    handleChange({
      monitor: sm,
      selectedAdditions: draftAdditions,
      deselectedDefaultAdditionIds: draftDeselectedDefaultAdditionIds,
    });
    setAdditionDialogOpen(false);
  }, [draftAdditions, draftDeselectedDefaultAdditionIds, handleChange, sm]);

  const handleMonitorDelete = useCallback(
    (monitor: RequirementMonitor) => {
      // Filter out the monitor to delete
      const updatedSelectedMonitors =
        putRequest.requirement.selectedMonitors.filter(
          (m) => m.id !== monitor.id,
        );

      // Update positions for the remaining monitors in this area
      const monitorsInArea = updatedSelectedMonitors.filter(
        (m) => m.areaId === monitor.areaId,
      );
      const reIndexedMonitors = updatedSelectedMonitors.map((m) => {
        if (m.areaId === monitor.areaId) {
          // Find index of this monitor in the area-filtered array
          const index = monitorsInArea.findIndex((am) => am.id === m.id);
          return {
            ...m,
            position: `${areaIndex}.${index + 1}`,
          };
        }
        return m;
      });

      updateRequirement({
        selectedMonitors: reIndexedMonitors,
      });
    },
    [putRequest.requirement.selectedMonitors, updateRequirement, areaIndex],
  );

  const handleAddSameMonitor = useCallback(() => {
    const duplicate: RequirementMonitor = {
      ...sm,
      id: v4(),
      amount: 1,
      amountOfMeasures: sm.amountOfMeasures || 0,
      existingStockAmount: 0,
      comment: "",
    };

    const selectedMonitors = [...putRequest.requirement.selectedMonitors];
    const currentMonitorIndex = selectedMonitors.findIndex(
      (m) => m.id === sm.id,
    );
    if (currentMonitorIndex < 0) {
      return;
    }
    selectedMonitors.splice(currentMonitorIndex + 1, 0, duplicate);

    let areaPosition = 1;
    const updatedMonitors = selectedMonitors.map((m) => {
      if (m.areaId !== sm.areaId) {
        return m;
      }
      const monitor = {
        ...m,
        position: `${areaIndex}.${areaPosition}`,
      };
      areaPosition += 1;
      return monitor;
    });

    updateRequirement({ selectedMonitors: updatedMonitors });
  }, [
    areaIndex,
    putRequest.requirement.selectedMonitors,
    sm,
    updateRequirement,
  ]);

  const iotClass = sm.ioTSpec?.class ?? monitorData.capabilities.iot?.class;
  const supportsCheckpointService =
    sm.monitorTypeKey === MonitorType.Checkpoint ||
    sm.monitorTypeKey === MonitorType.FlyExterminator ||
    (sm.monitorTypeKey === MonitorType.IoT &&
      supportsIoTCheckpointService(iotClass));
  const hasCheckpointServiceInBaseData =
    monitorData.capabilities.includeInCheckpointService;
  const canConfigureCheckpointService =
    supportsCheckpointService && hasCheckpointServiceInBaseData;
  const includesCheckpointService: CheckedState =
    sm.includeInCheckpointService === true
      ? true
      : sm.includeInCheckpointService === false
        ? false
        : "indeterminate";

  return (
    <TableRow>
      <TableCell className="py-1 font-bold">{sm.position}</TableCell>
      <TableCell className="py-1">{monitorData.name}</TableCell>
      <TableCell className="py-1">
        {MonitorTypeUtil.toString(monitorData.type)}
      </TableCell>
      <TableCell className="py-1 text-right">
        <div className="flex justify-end">
          <Input
            id={`${sm.id}_main`}
            className="h-8 w-24 text-end"
            type="number"
            min={0}
            value={sm.amount}
            onChange={(e) =>
              handleChange({
                monitor: sm,
                amount: Number.isNaN(parseInt(e.target.value, 10))
                  ? 0
                  : parseInt(e.target.value, 10),
              })
            }
          />
        </div>
      </TableCell>
      <TableCell className="py-1 text-right">
        <div className="flex justify-end">
          <Input
            id={`${sm.id}_part`}
            className="h-8 w-24 text-end"
            type="number"
            min={0}
            max={sm.amount}
            value={sm.existingStockAmount || 0}
            onChange={(e) =>
              handleChange({
                monitor: sm,
                existingStockAmount: Number.isNaN(parseInt(e.target.value, 10))
                  ? 0
                  : parseInt(e.target.value, 10),
              })
            }
          />
        </div>
      </TableCell>
      <TableCell className="py-1">
        {availableAdditions.length > 0 ? (
          <div className="flex max-w-80 min-w-56 items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap gap-1">
              {effectiveSelectedAdditions.length > 0 ? (
                effectiveSelectedAdditions.map((selection) => {
                  const additional = availableAdditions.find(
                    (item) => item.id === selection.additionalId,
                  );
                  return (
                    <Badge
                      key={selection.additionalId}
                      variant="secondary"
                      className="max-w-full"
                    >
                      <span className="truncate">
                        {additional?.name ?? selection.additionalId}
                      </span>
                      <span className="shrink-0">
                        · {additionDisplayAmount(selection)}
                      </span>
                    </Badge>
                  );
                })
              ) : (
                <span className="text-muted-foreground text-xs">
                  {t("Keine Zusätze")}
                </span>
              )}
            </div>
            <Dialog
              open={additionDialogOpen}
              onOpenChange={handleAdditionDialogOpenChange}
            >
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0 gap-2">
                  <Package className="h-4 w-4" />
                  {t("Zusätze")}
                </Button>
              </DialogTrigger>
              <DialogContent className="flex max-h-[88vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
                <DialogHeader className="border-b px-6 py-5">
                  <DialogTitle>{t("Material-Zusätze")}</DialogTitle>
                  <DialogDescription>
                    {monitorData.name} · {sm.position}
                  </DialogDescription>
                </DialogHeader>
                <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">
                      {t("Verfügbare Zusätze")}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t(
                        "Tippen Sie einen Zusatz an, um ihn für diese Monitorposition auszuwählen.",
                      )}
                    </div>
                    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                      {availableAdditions.length === 0 ? (
                        <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm md:col-span-2 xl:col-span-3">
                          {t("Keine Zusätze verfügbar")}
                        </div>
                      ) : (
                        availableAdditions.map((additional) => {
                          const selected = draftAdditions.some(
                            (item) => item.additionalId === additional.id,
                          );
                          const config = normalizeMonitorAdditionalConfig(
                            additional.config,
                          );
                          return (
                            <Button
                              key={additional.id}
                              type="button"
                              variant="ghost"
                              className={`flex h-auto min-h-16 w-full items-center justify-between gap-3 rounded-md border p-3 text-left transition-colors ${
                                selected
                                  ? "border-primary bg-primary/5"
                                  : "hover:bg-muted/40"
                              }`}
                              onClick={() =>
                                handleDraftAdditionSelect(additional.id)
                              }
                            >
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium">
                                  {additional.name}
                                </span>
                                {additional.description ? (
                                  <span className="text-muted-foreground line-clamp-2 text-xs">
                                    {additional.description}
                                  </span>
                                ) : null}
                                {config.selectionMode === "ALWAYS_REQUIRED" ? (
                                  <span className="text-muted-foreground block text-xs">
                                    {t("Immer erforderlich")}
                                  </span>
                                ) : config.selectionMode ===
                                  "DEFAULT_SELECTED_OPTIONAL" ? (
                                  <span className="text-muted-foreground block text-xs">
                                    {t("Standardmäßig ausgewählt")}
                                  </span>
                                ) : null}
                              </span>
                              {selected ? (
                                <Check className="text-primary h-4 w-4 shrink-0" />
                              ) : (
                                <Package className="text-muted-foreground h-4 w-4 shrink-0" />
                              )}
                            </Button>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">
                      {t("Ausgewählte Zusätze")}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t(
                        "Die Menge ist standardmäßig die Monitoranzahl abzüglich Altbestand.",
                      )}
                    </div>
                    {draftAdditions.length === 0 ? (
                      <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
                        {t("Keine Zusätze ausgewählt")}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {draftAdditions.map((selection) => {
                          const additional = availableAdditions.find(
                            (item) => item.id === selection.additionalId,
                          );
                          if (!additional) {
                            return null;
                          }
                          const displayAmount =
                            additionDisplayAmount(selection);
                          const isSeparate =
                            selection.materialMode === "SEPARATE_MATERIAL";
                          const config = normalizeMonitorAdditionalConfig(
                            additional.config,
                          );
                          const isAlwaysRequired =
                            config.selectionMode === "ALWAYS_REQUIRED";
                          return (
                            <div
                              key={selection.additionalId}
                              className="grid min-h-16 grid-cols-[minmax(0,1fr)_7rem_2.5rem] items-center gap-3 rounded-md border p-3 md:grid-cols-[minmax(0,1fr)_10rem_2.5rem]"
                            >
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium">
                                  {additional.name}
                                </div>
                                <div className="text-muted-foreground truncate text-xs">
                                  {isSeparate
                                    ? t("Eigene Materialmenge")
                                    : t("Folgt der Materialmenge")}
                                </div>
                              </div>
                              <Input
                                className="h-8 text-end"
                                type="number"
                                min={0}
                                value={displayAmount}
                                onChange={(e) => {
                                  const nextAmount = Number.isNaN(
                                    parseInt(e.target.value, 10),
                                  )
                                    ? 0
                                    : parseInt(e.target.value, 10);
                                  handleDraftAdditionAmountChange(
                                    selection.additionalId,
                                    nextAmount,
                                  );
                                }}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10"
                                aria-label={t("Zusatz entfernen")}
                                disabled={isAlwaysRequired}
                                onClick={() =>
                                  handleDraftAdditionRemove(
                                    selection.additionalId,
                                  )
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter className="border-t px-6 py-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAdditionDialogOpen(false)}
                  >
                    {t("Abbrechen")}
                  </Button>
                  <Button type="button" onClick={handleAdditionDialogConfirm}>
                    {t("Bestätigen")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )}
      </TableCell>
      <TableCell className="py-1 text-center">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              {sm.comment ? t("Bearbeiten") : t("Bemerkung")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{t("Bemerkung")}</DialogTitle>
              <DialogDescription>
                {monitorData.name} · {sm.position}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              className="min-h-40"
              value={sm.comment}
              onChange={(e) =>
                handleChange({ monitor: sm, comment: e.target.value })
              }
              placeholder={t("Bemerkung")}
            />
          </DialogContent>
        </Dialog>
      </TableCell>
      <TableCell className="py-1 text-right">
        <div className="flex justify-end">
          <Input
            id={`${sm.id}_measures`}
            className="h-8 w-24 text-end"
            type="number"
            min={0}
            value={sm.amountOfMeasures || 0}
            onChange={(e) =>
              handleChange({
                monitor: sm,
                amountOfMeasures: Number.isNaN(parseInt(e.target.value, 10))
                  ? 0
                  : parseInt(e.target.value, 10),
              })
            }
          />
        </div>
      </TableCell>
      <TableCell className="py-1">
        <div className="flex justify-center">
          {canConfigureCheckpointService ? (
            <Checkbox
              checked={includesCheckpointService}
              onCheckedChange={(value) =>
                handleChange({
                  monitor: {
                    ...sm,
                    includeInCheckpointService: value === true,
                  },
                })
              }
            />
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="w-fit text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleAddSameMonitor}>
              {t("Gleichen Monitor hinzufügen")}
            </DropdownMenuItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={(e) => e.preventDefault()}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("Monitor entfernen")}
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("Monitor aus dem Bereich entfernen?")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t(
                      `Sind sie sich sicher, dass sie ${sm.amount} ${monitorData.name} entfernen möchten?`,
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Abbruch")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleMonitorDelete(sm)}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {t("Bestätigen")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
